import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import useNotify from '../hooks/useNotify';
import { useGetVenues } from '@openapi/venues/venues';
import { useGetShows } from '@openapi/shows/shows';
import { addBulkTicketSeats } from '@openapi/ticket-seats/ticket-seats';
import mapElementRowToDb from '../pages/seats/helpers/mapElementRowToDb';

const SeatingCloneModal = ({ showModal, MODAL_TYPE, showId, elements }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const [venueId, setVenueId] = useState(null);
    const [selectedShowId, setSelectedShowId] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const { data: venueData } = useGetVenues({}, { query: { onError: notifyError } });
    const allVenues = venueData?.venues || [];

    const { data: showsData, isLoading } = useGetShows(
        { pageSize: 1000, includeSeatCount: true },
        {
            query: {
                onError: notifyError,
            },
        },
    );
    const globalShows = showsData?.shows || [];

    const globalShowsWithNoSeats = globalShows.filter(
        (show) => show.seatCount === 0 && Number(show.id) !== Number(showId),
    );

    const availableVenueIds = new Set(globalShowsWithNoSeats.map((s) => s.venueId));
    const venues = allVenues.filter((v) => availableVenueIds.has(v.id));

    const showsForSelectedVenue = globalShowsWithNoSeats.filter((show) => show.venueId === venueId);

    useEffect(() => {
        if (!venueId && venues.length > 0) {
            setVenueId(venues[0].id);
        } else if (venueId && venues.length > 0 && !venues.some((v) => v.id === venueId)) {
            setVenueId(venues[0].id);
        }
    }, [venues, venueId]);

    useEffect(() => {
        if (selectedShowId === '') return;
        if (!showsForSelectedVenue.some((show) => show.id === selectedShowId)) {
            setSelectedShowId('');
        }
    }, [showsForSelectedVenue, selectedShowId]);

    const handleConfirm = async () => {
        if (!selectedShowId) return;

        setIsProcessing(true);
        try {
            await addBulkTicketSeats({
                showId: selectedShowId,
                seats: elements.map((element) => {
                    const { id: _id, ...payload } = mapElementRowToDb(element);
                    return payload;
                }),
            });

            notifySuccess(t('seats.cloneSuccess'));
            closeAction();
        } catch (error) {
            notifyError(error?.message || t('errors.unknownError'));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="seating-clone-modal-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="seating-clone-modal-title">{t('seats.cloneTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('seats.cloneDescription')}
                    </Typography>

                    <FormControl fullWidth size="small">
                        <InputLabel id="clone-venue-select-label">
                            {t('reports.filters.venue')}
                        </InputLabel>
                        <Select
                            labelId="clone-venue-select-label"
                            label={t('reports.filters.venue')}
                            value={venueId || ''}
                            onChange={(e) => setVenueId(e.target.value)}
                            disabled={isLoading || venues.length === 0}
                        >
                            {venues.map((venue) => (
                                <MenuItem key={venue.id} value={venue.id}>
                                    {venue.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="clone-show-select-label">
                            {t('reports.filters.show')}
                        </InputLabel>
                        <Select
                            labelId="clone-show-select-label"
                            label={t('reports.filters.show')}
                            placeholder={t('invoices.selectShow')}
                            value={selectedShowId}
                            onChange={(e) => setSelectedShowId(e.target.value)}
                            disabled={isLoading || showsForSelectedVenue.length === 0}
                        >
                            {showsForSelectedVenue.map((show) => (
                                <MenuItem key={show.id} value={show.id}>
                                    {show.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={handleConfirm}
                    disabled={!selectedShowId || isProcessing}
                    label={t('common.save')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
};

export default SeatingCloneModal;
